/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import React from "react";
import { useMutation } from "@apollo/client";
import css from "classnames";
import { gql } from "graphql-tag";
import { Select } from "components/haven-design/form/select";
import inputStyles from "components/haven-design/form/inputs/input.module.css";
import { MoveInModal } from "pages/dashboard/interests-profile-slideout/components/modals/move-in";

import { ChangeInterestStageProfile, ChangeInterestStageProfileVariables, QueryInterestProfile_interest as InterestType } from "types/gql-types";
import { useSnackbar } from "components/haven-design/snackbar";
import styles from "../details.module.css";
import ReasonModal, { Action } from "../reason-modal";

import Heading from "./heading";

const INACTIVE_STAGE_NAMES = ["Removed", "Moved In", "Reinstatement Requested"];

const CHANGE_STATUS = gql`
  mutation ChangeInterestStageProfile($interestId: ID!, $status: String!, $reason: String) {
    changeInterestStatus(interestId: $interestId, status: $status, reason: $reason)
  }
`;

const needsReason = (value: string, currentStatus: string): boolean =>
  [currentStatus, value].some((val) => INACTIVE_STAGE_NAMES.includes(val));

type Props = { interest: InterestType }

const ProfileStatusManager: React.FC<Props> = ({ interest }) => {
  const [changeStage, mutationState] =
  useMutation<ChangeInterestStageProfile, ChangeInterestStageProfileVariables>(CHANGE_STATUS, {
    refetchQueries: ["activityFeed", "QueryInterestProfile"],
  });
  const [reason, setReason] = React.useState("");

  const [newStatus, setNewStatus] = React.useState<Action | null>(null);
  const [showModal, setShowModal] = React.useState(false);

  const { openSnackbar } = useSnackbar();

  const {
    status: { name: interestStatus },
    interestList: { property: { stages } },
  } = interest;

  const enabledStages = stages.filter((stage) => stage.enabled);

  const resetModal = () => {
    setReason("");
    setShowModal(false);
  };

  const changeStatus = (status: string) => {
    changeStage({
      variables: {
        interestId: interest.id,
        status,
        reason,
      },
    })
      .then(() => openSnackbar({ message: "Updated application status.", status: "success" }))
      .then(() => resetModal());
  };

  const handleChange = ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>) => {
    setNewStatus(value as Action);

    if (needsReason(value, interestStatus)) {
      setShowModal(true);
    } else {
      changeStatus(value);
    }
  };

  return (<>
    <div className={styles.group}>
      <Heading text="Status" />
      {
        <div style={{ marginTop: 8 }}>
          <Select
            className={css(inputStyles.input, inputStyles.small)}
            disabled={mutationState.loading}
            value={interest.status.name}
            onChange={handleChange}
          >
            {enabledStages
              .map(({ name: stage }) => (
                <option key={stage} value={stage}>
                  {stage}
                </option>
              ))}
          </Select>
        </div>
      }
    </div>

    {newStatus && (
      <ReasonModal
        onRequestClose={() => resetModal()}
        onSubmit={() => changeStatus(newStatus)}
        onCancel={() => resetModal()}
        onChange={setReason}
        isOpen={showModal && newStatus !== "Moved In"}
        isSubmitting={mutationState.loading}
        newStatus={newStatus}
        currentStatus={interestStatus as Action}
        reason={reason}
      />
    )}
    {interest?.interestList &&
      <MoveInModal
        isOpen={showModal && newStatus === "Moved In"}
        onRequestClose={resetModal}
        interestIds={[interest.id]}
        interestListId={interest.interestList.id}
      />}

  </>);
};
export default ProfileStatusManager;
